define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dgrid/Grid",
	"csi/_base/underscore",
	"csi/form/Widget",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/xml"
], function(declare, lang, Grid, _, FormWidget, html, locale, reflection, xml) {

	var EXP1 = "validValue('SourcingPasteData')",
		EXP2 = "!" + EXP1;

	return declare(FormWidget, {
		ID_COLUMN: "Id",
		VALID_CLASSES: {
			"SupplierItem": true,
			"SupplierItemRevision": true
		},
		VALID_ATTRIBUTE_TYPES: {
			"string": true,
			"double": true,
			"integer": true,
			"time": true,
			"float": true,
			"short": true,
			"int64": true
		},

		DATA: "SourcingPasteData",

		_hideExpressions: {
			SourcingPasteData: EXP1
		},

		letterRegExp: new RegExp("\\w"),
		digitRegExp: new RegExp("\\d"),

		// remember parsed data rows to add to server call
		_dataRows: null,

		_updatableAttributes: {},
		_duplicateAttributes: {},
		_duplicateRows: {},
		_styleCleanUp: [],

		_buildView: function() {
			this.inherited(arguments);

			// disable save button after form is built, but before it is rendered
			this.disableSave(true);
		},

		_onFadeBody: function() {
			this.inherited(arguments);

			// disable save after the buttons are reactivated on a reset
			this.disableSave(true);
		},

		getFormContext: function() {
			// summary: add attributes to support _hideExpressions
			var ctx = this.inherited(arguments);
			var node = ctx.getNode();
			xml.nodeCreateClientAttr(node, this.DATA, xml.ATTR_STRING, this.defView.getFieldValue(this.DATA));
			return ctx;
		},

		_postView: function() {
			// summary: only allow Ctrl+V to paste data and watch for when it happens
			var w = this.defView.getFieldControl(this.DATA);

			w.onInput = function(evt) {
				return html.ctrlKeyPressed(evt) && (!evt.charOrCode || (evt.charOrCode == "V"));
			};

			w.on("paste", lang.hitch(this, this._onWatchData));

			this.inherited(arguments);
		},

		_pasteChartInfo: function(/*String*/message) {
			var commentNode = this.bodyNode.firstChild.firstChild;
			var infoList = html.create("ul", {"class": "pasteChartInfo"}, commentNode);
			for (var i = 0; i < message.length; i++) {
				html.create("li", {multiLineText: message[i]}, infoList);
			}
		},

		/**
		 * parse data to see if it is valid - if so, then convert to HTML table and display other fields
		 * @param {Event} e - paste event
		 */
		_onWatchData: function(e) {
			const newValue = e.clipboardData.getData('text')
			if (!newValue) return;

			try {
				this._dataRows = this._parseData(newValue);

				// create stores for dimension and name indices
				var cols = this._dataRows[0];

				// render HTML to show data
				var columns = _.map(cols, function(col, i) {return {label: col, field: i, sortable: false};});
				var commentNode = this.bodyNode.firstChild.firstChild;

				// add conditional class for duplicate Ids
				columns[0].className = lang.hitch(this, function(row) {
					if (row && this._duplicateRows[row[0]]) {
						return "duplicateRow";
					}
					return;
				});

				html.clear(commentNode);

				var grid = new Grid({
					"class": "formPasteGrid",
					columns: columns,
					id: "dgrid_pasteGrid"
				}, html.create("div", null, commentNode));

				// Loop through and style columns based on various conditions:
				// bold all column headers that were discovered to be updatable
				// highlight updatable, but duplicate columns red
				var duplicateCount = 0;
				for (var i = 0; i < grid._columns.length; i++) {
					var key = grid.column(i).label;
					if (this._updatableAttributes[key]) {
						grid.column(i).headerNode.className += " boldHeader";
					} else if (this._duplicateAttributes[key]) {
						delete this._updatableAttributes[key];
						duplicateCount++;
						this._styleCleanUp.push(grid.styleColumn(i, "background-color: red"));
					}
				}

				grid.renderArray(this._dataRows.slice(1));

				var duplicateRowKeys = [];
				for (key in this._duplicateRows) {
					if (this._duplicateRows[key]) {
						duplicateRowKeys.push(key);
					}
				}

				if (duplicateRowKeys.length > 0) {
					var duplicateRowsInfo = locale.resource("DuplicateRowsFound", [duplicateRowKeys.length, duplicateRowKeys]);
				}

				// Call resize to account for the newly bolded headers
				grid.resize();

				var duplicateAttributesInfo = "";
				if (duplicateCount > 0) {
					duplicateAttributesInfo = locale.resource("DuplicateColumnsFound", [duplicateCount]);
				}

				var updatableAttrCount = _.size(this._updatableAttributes);
				var updatableAttrInfo = locale.resource("ColumnsWillBeImported" + (updatableAttrCount == 1 ? "Singular" : "Plural"), [updatableAttrCount]);

				if (_.size(this._updatableAttributes) < 1) {
					// no updatable attributes found
					this._pasteChartInfo([locale.resource("SourcingPasteDataNoUpdatableAttributesError")]);
				} else {
					this._pasteChartInfo([updatableAttrInfo, duplicateAttributesInfo, duplicateRowsInfo]);
				}

				if (updatableAttrCount > 0 && duplicateCount < 1) {
					this.disableSave(false);
				}

				// set the field value to trigger change to visibility of other fields
				this.invalidNode.set("value", "");
				this.setFieldValue(this.DATA, newValue);
			} catch (ex) {
				// show error for given input
				this.invalidNode.set("value", ex.message);
				this.setFieldValue(this.DATA, "");
			}
		},

		_parseData: function(/*String*/data) {
			// summary: parse and validate entered data against table
			var err = function(s) {throw new Error(locale.resource(s));};

			// parse data into rows, skipping empty rows
			var rows = data.split("\n").filter((s) => !!s.trim());

			// check for at least 2 rows
			if (rows.length < 2) {
				err("SourcingPasteErrorRows");
			}

			// parse rows into columns, first compressing multiple blanks into single blanks
			var separator = this._inferSeparator(rows);
			rows = _.map(rows, function(row) {return row.replace(/[ ]+/g, " ").split(separator);});

			// check each column header to determine which attributes can be updated
			var columnHeaders = rows[0];
			if (columnHeaders[0] !== this.ID_COLUMN) {
				err("SourcingPasteDataNoIdError");
			}

			this._findUpdatableAttributes(columnHeaders);
			this._findDuplicateRows(rows);

			// check that all rows have same number of columns
			var numCols = rows[0].length;
			if (_.some(rows, function(cols) {return cols.length != numCols;})) {
				err("SourcingPasteErrorRowsAndColumns");
			}

			return rows;
		},

		_findUpdatableAttributes: function(/*Array*/columnHeaders) {
			// iterate through all classes in the data to see if there is at least one updatable attribute
			this._updatableAttributes = {};
			this._duplicateAttributes = {};
			for (var i = 1; i < columnHeaders.length; i++) {
				var header = columnHeaders[i];
				var headerClass = header.substr(0, header.indexOf(' '));

				// check that the attribute is either from SupplierItem or SupplierItemRevision as anything else is not editable from offline
				// CONSIDERATION: consider using if (Object.prototype.hasOwnProperty.call(obj, A)) to be more scope-specific.
				if (headerClass in this.VALID_CLASSES) {
					var attributeName = header.substr(header.indexOf(' ') + 1).replace("\r", "");
					var foundClass = reflection.getClass(headerClass);
					var foundAttribute = foundClass.getAttribute(attributeName, true, true, true); // true flags for super, derived, and relationship classes

					// mark as updatable if the class is not read only and of a correct data type
					if (foundAttribute && !foundAttribute.isReadOnly && foundAttribute.m_type in this.VALID_ATTRIBUTE_TYPES) {
						if (Object.prototype.hasOwnProperty.call(this._updatableAttributes, header)) {
							this._updatableAttributes[header] = false;
							this._duplicateAttributes[header] = (this._duplicateAttributes[header] || 1) + 1;
						} else {
							this._updatableAttributes[header] = true;
						}
					}
				}
			}
		},

		_findDuplicateRows: function(/*Array*/rows) {
			this._duplicateRows = {};
			for (var i = 1; i < rows.length; i++) {
				var id = rows[i][0];
				if (this._duplicateRows[id] === false) {
					// seen Id before, it's a duplicate
					this._duplicateRows[id] = true;
				} else if (!Object.prototype.hasOwnProperty.call(this._duplicateRows, id)) {
					// never seen the Id before
					this._duplicateRows[id] = false;
				}
			}
		},

		_inferSeparator: function(/*String[]*/rows) {
			// summary: figure out what character is being used as the value separator
			var err = function(s) {throw new Error(locale.resource(s));};

			var headingsRow = rows[0];
			var dataRow1 = rows[1];
			var candidates = this._findCandidateSeparators(headingsRow);
			candidates = this._intersect(candidates, this._findCandidateSeparators(dataRow1));

			var bestCandidate = null;
			var maxSplitSize = 0;
			for (var candidate in candidates) {
				var headings = headingsRow.split(candidate);
				var data1 = dataRow1.split(candidate);
				if (headings.length == data1.length && headings.length > maxSplitSize) {
					bestCandidate = candidate;
					maxSplitSize = headings.length;
				}
			}

			if (bestCandidate) {
				return bestCandidate;
			} else {
				err("SourcingPasteErrorNoClearSeparator");
			}
		},

		_findCandidateSeparators: function(/*String*/row) {
			// summary: return the set of non-alphanumeric characters in row
			var candidates = {};
			for (var i = 0; i < row.length; i++) {
				var ch = row.charAt(i);
				if (!this._isLetterOrDigit(ch) && !(ch == " ")) {
					candidates[ch] = true;
				}
			}
			return candidates;
		},

		_isLetterOrDigit: function(/*String*/ch) {
			return this.letterRegExp.test(ch) || this.digitRegExp.test(ch);
		},

		_intersect: function(/*Object*/set1, /*Object*/set2) {
			var result = {};
			for (var elem in set1) {
				if (set2[elem]) {
					result[elem] = true;
				}
			}
			return result;
		},

		validateForm: function(/*Object*/errorParams) {
			// summary: make Data value available to action controller
			var val = this.inherited(arguments);
			if (this._dataRows) {
				this.defView.m_formData.setFieldInitialValue("Data", this._reassembleData(this._dataRows));
				this.defView.m_formData.setFieldInitialValue("RowCount", this._dataRows.length - 1);
			}
			return val;
		},

		_reassembleData: function(rows) {
			// summary: pack the parsed data back into a string that's easy for the server to parse
			// Get the updatable attribute locations
			var updateIndex = [];
			_.each(rows[0], function(element, index) {
				if (this._updatableAttributes[element] || index === 0) {
					updateIndex.push(index);
				}
			}, this);

			// Manually build out the rows, skipping what isn't updatable
			var newRows = [];
			_.each(rows, function(row, index) {
				_.each(updateIndex, function(element) {
					newRows[index] = newRows[index] ? newRows[index] + ("\t" + row[element]) : row[element];
				});
			});

			// Join the rows with newlines and return
			return newRows.join("\n");
		},

		resetForm: function() {
			// hook into resetForm and cleanup residual styles from dgrid
			for (var i = 0; i < this._styleCleanUp.length; i++) {
				this._styleCleanUp[i].remove();
			}
			this.inherited(arguments);
		}
	});
});